import React from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { BylineConnectionsPlaceholderVisual } from '../BylineConnectionsPlaceholderVisual.tsx';

import { getReservedBylineIconCount } from './outboundBylineSsrState.ts';
import { useIncomingLinksSsrState } from './useIncomingLinksSsrState.ts';
import { useOutboundBylineSsrState } from './useOutboundBylineSsrState.ts';

type BylineConnectionsPlaceholderProps = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export const BylineConnectionsPlaceholder = ({
	contentId,
	connectionAncestors,
}: BylineConnectionsPlaceholderProps): React.JSX.Element | null => {
	// Read the preloaded outbound state on the client too (cache-only, hydrated from the SSR
	// preload). This lets the placeholder stay visible after the post-paint hydration and while the
	// LoadableAfterPaint chunk loads, instead of blanking — which caused the "SSR placeholder →
	// nothing → skeleton → item" flash. The result is derived purely from the hydrated cache and
	// dev-docs, so it is identical on the server and the client's first render (no hydration
	// mismatch). Pages with no connections still render nothing (no client footprint). CCINT-8752
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingLinksState = useIncomingLinksSsrState(contentId);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const hasOutboundSignal =
		outboundState?.status === 'present' ||
		outboundState?.status === 'unavailable' ||
		devDocsConnection !== null;

	if (!hasOutboundSignal) {
		// Inbound-only: with no outbound smart-link or Dev Docs signal, render the text-only
		// placeholder (label, no icon tiles) when the bounded inbound sample found incoming
		// connections. This matches the inbound-only FE trigger, whose icon stack is empty
		// (BylineConnectionIconStack returns null for zero links). Any other inbound state — empty,
		// unavailable, or a cache miss — keeps zero footprint. CCINT-8932
		if (incomingLinksState?.status === 'present') {
			return (
				<BylineConnectionsPlaceholderVisual
					testId="byline-connections-ssr-placeholder"
					iconCount={0}
				/>
			);
		}
		return null;
	}

	const iconCount = getReservedBylineIconCount(outboundState, devDocsConnection !== null);

	return (
		<BylineConnectionsPlaceholderVisual
			testId="byline-connections-ssr-placeholder"
			iconCount={iconCount}
		/>
	);
};
